/**
 * Shared client-side evaluation of attribute trigger rules (display / required toggles).
 *
 * Both consumers (formOptionRules, formRequiredTriggers) describe rules the same way — an
 * OR-ed list of AND-ed condition lists — but locate the trigger control and read the value a
 * condition compares against from different fields. The caller passes a `resolveCondition`
 * mapper returning { $trigger, expectedValue } for a single condition.
 */

const isCheckable = function ($el) {
	const type = ($el.attr('type') || '').toLowerCase();
	return type === 'checkbox' || type === 'radio';
};

const isFilled = function ($trigger) {
	if (!$trigger.length) return false;

	const anyCheckable = $trigger.filter(function () {
		const type = (this.getAttribute('type') || '').toLowerCase();
		return type === 'checkbox' || type === 'radio';
	});

	if (anyCheckable.length) {
		return anyCheckable.is(':checked');
	}

	return $.trim($trigger.first().val() || '') !== '';
};

const getValue = function ($trigger) {
	if (!$trigger.length) return '';

	const $radios = $trigger.filter('[type="radio"]');
	if ($radios.length) {
		const $checked = $radios.filter(':checked');
		return $checked.length ? String($checked.val() || '') : '';
	}

	const $checkboxes = $trigger.filter('[type="checkbox"]');
	if ($checkboxes.length) {
		return $checkboxes.filter(':checked').map(function () {
			return String($(this).val() || '1');
		}).get();
	}

	if (isCheckable($trigger.first())) {
		return $trigger.first().is(':checked') ? String($trigger.first().val() || '1') : '';
	}

	return String($trigger.first().val() || '');
};

const equal = function ($trigger, expectedValue) {
	const expected = String(expectedValue ?? '');
	const value = getValue($trigger);

	return Array.isArray(value)
		? value.includes(expected)
		: String(value) === expected;
};

const evaluateRuleConditions = function (conditionList, resolveCondition) {
	for (const condition of conditionList) {
		const { $trigger, expectedValue } = resolveCondition(condition);

		if ($trigger.length === 0) {
			return false;
		}

		if (condition.operator === 'filled' && isFilled($trigger) === false) {
			return false;
		}

		if (condition.operator === 'blank' && isFilled($trigger) === true) {
			return false;
		}

		if (condition.operator === 'equal' && equal($trigger, expectedValue) === false) {
			return false;
		}

		if (condition.operator === 'notEqual' && equal($trigger, expectedValue) === true) {
			return false;
		}
	}

	return true;
};

/**
 * @param {Array}    ruleList         OR-ed list of AND-ed condition lists.
 * @param {Function} resolveCondition Maps a condition to { $trigger, expectedValue }.
 * @returns {boolean}                 True when at least one rule is fully satisfied.
 */
export const evaluate = function (ruleList, resolveCondition) {
	for (const conditionList of ruleList) {
		if (evaluateRuleConditions(conditionList, resolveCondition) === true) {
			return true;
		}
	}

	return false;
};
